/**
 * Lead Scoring Settings (tenant admin).
 * Configures THIS workspace's lead-scoring rules. Until saved, the workspace
 * inherits the platform default; saving creates its own override, and "Reset to
 * platform default" removes it again. Saving re-scores every lead straight away.
 *
 * Layout: rules read as sentences ("If <field> <condition> <value> → +N pts") in a
 * clean list, new rules are added at the TOP and focused (the old "Add rule" appended
 * to the bottom of a long list, off-screen, so it looked broken), and the priority
 * thresholds are shown as a single 0–100 band bar.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Add, DeleteOutline, RestartAlt, Save } from '@mui/icons-material';
import StatusChip from '@/components/ui/StatusChip';
import adminApi, {
  FieldDefinition,
  LeadScoringConfig as LeadScoringConfigType,
  ScoringCondition,
  ScoringGroup,
  ScoringRuleInput,
} from '@/services/admin.api';

const CONDITIONS: ScoringCondition[] = ['exists', 'equals', 'gt', 'gte', 'lt', 'lte', 'contains', 'in'];
const NUMERIC_CONDS: ScoringCondition[] = ['gt', 'gte', 'lt', 'lte'];

/** Plain-English labels — the stored values (exists / gte / in…) are engine keywords. */
const CONDITION_LABEL: Record<ScoringCondition, string> = {
  exists: 'has a value',
  equals: 'equals',
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  contains: 'contains',
  in: 'is one of',
};

// Core lead fields that scoring can match on (exact keys the engine reads). The
// tenant's own custom/vertical attribute keys are appended at runtime. Kept free-solo
// so a not-yet-seen attribute key can still be typed.
const CORE_LEAD_FIELDS = [
  'customerName',
  'customerEmail',
  'customerPhone',
  'customerCompany',
  'customerCountry',
  'interest',
  'budget',
  'estimatedValue',
  'notes',
  'status',
  'source',
  'assignedTo',
  'tags',
  'chatEngaged',
  'chatTurns',
  // Computed by the engine, not stored on the lead.
  'daysSinceLastContact',
  'daysSinceCreated',
];

/** Real enums for the fields where a free-text value would silently never match. */
const ENUM_FIELD_VALUES: Record<string, string[]> = {
  status: ['new', 'contacted', 'qualified', 'unqualified', 'converted', 'lost', 'archived'],
  source: ['website', 'whatsapp', 'meta', 'hubspot', 'google_sheets', 'excel', 'api', 'manual'],
};

type FieldKind = 'number' | 'boolean' | 'text' | 'date' | 'unknown';

/** Core fields whose type is fixed by the engine, not by a tenant definition. */
const CORE_FIELD_KIND: Record<string, FieldKind> = {
  estimatedValue: 'number',
  chatTurns: 'number',
  daysSinceLastContact: 'number',
  daysSinceCreated: 'number',
  chatEngaged: 'boolean',
  status: 'text',
  source: 'text',
  interest: 'text',
  tags: 'text',
};

/** Conditions that make sense for each kind — a text field can't be "greater than". */
const KIND_CONDITIONS: Record<FieldKind, ScoringCondition[]> = {
  number: ['exists', 'equals', 'gt', 'gte', 'lt', 'lte'],
  boolean: ['exists', 'equals'],
  text: ['exists', 'equals', 'contains', 'in'],
  date: ['exists'],
  unknown: CONDITIONS,
};

const BOOLEAN_VALUES = ['true', 'false'];

const GROUPS: Array<{ value: ScoringGroup; label: string }> = [
  { value: 'fit', label: 'Fit' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'pipeline', label: 'Pipeline' },
];

const BAND = {
  low: { bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8', label: 'Low' },
  medium: { bg: '#dbeafe', fg: '#1d4ed8', dot: '#3b82f6', label: 'Medium' },
  high: { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b', label: 'High' },
  hot: { bg: '#fee2e2', fg: '#b91c1c', dot: '#ef4444', label: 'Hot' },
} as const;

interface RuleRow {
  group: ScoringGroup;
  field: string;
  condition: ScoringCondition;
  valueStr: string;
  score: number;
  description: string;
}

function toRuleRow(r: ScoringRuleInput): RuleRow {
  let valueStr = '';
  if (Array.isArray(r.value)) valueStr = r.value.join(', ');
  else if (r.value !== undefined && r.value !== null) valueStr = String(r.value);
  return {
    group: r.group ?? 'fit',
    field: r.field ?? '',
    condition: r.condition ?? 'exists',
    valueStr,
    score: Number(r.score) || 0,
    description: r.description ?? '',
  };
}

function fromRuleRow(row: RuleRow): ScoringRuleInput {
  const base: ScoringRuleInput = {
    group: row.group,
    field: row.field.trim(),
    condition: row.condition,
    score: Number(row.score) || 0,
    ...(row.description.trim() ? { description: row.description.trim() } : {}),
  };
  const v = row.valueStr.trim();
  if (row.condition === 'exists') return base;
  if (row.condition === 'in') {
    return { ...base, value: v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [] };
  }
  if (NUMERIC_CONDS.includes(row.condition)) return { ...base, value: Number(v) };
  return { ...base, value: v };
}

/** One input look for the whole page: soft fill, hairline border, 36px tall, no number spinners. */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    height: 38,
    borderRadius: '10px',
    bgcolor: '#f8fafc',
    fontSize: '0.875rem',
    transition: 'background-color .15s, box-shadow .15s',
    '& fieldset': { borderColor: '#e6eaf1' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused': { bgcolor: '#fff', boxShadow: `0 0 0 3px ${alpha('#4f46e5', 0.12)}` },
    '&.Mui-focused fieldset': { borderColor: '#4f46e5', borderWidth: 1 },
    '&.Mui-disabled': { bgcolor: '#f1f5f9' },
  },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
} as const;

const popperProps = { popper: { style: { width: 'fit-content', minWidth: 260 } } } as const;

const RULE_GRID = { xs: '1fr', md: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,1.2fr) 112px 36px' };

const HeaderCell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}
  >
    {children}
  </Typography>
);

const SectionTitle: React.FC<{ title: string; hint?: string; action?: React.ReactNode }> = ({
  title,
  hint,
  action,
}) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9375rem' }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

const LeadScoringSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [isCustom, setIsCustom] = useState(false);

  const [rules, setRules] = useState<RuleRow[]>([]);
  const [stats, setStats] = useState<{ totalLeads: number; byPriority: Record<string, number> } | null>(null);
  const [rescoring, setRescoring] = useState(false);
  const [rescoreMsg, setRescoreMsg] = useState('');
  const [missingDefaults, setMissingDefaults] = useState<ScoringRuleInput[]>([]);
  const [hot, setHot] = useState(80);
  const [high, setHigh] = useState(60);
  const [medium, setMedium] = useState(40);
  const [recencyDecayPerDay, setRecencyDecayPerDay] = useState(0.5);
  const [customFields, setCustomFields] = useState<string[]>([]);
  // Index of a row that was just added, so its Field input grabs focus.
  const [focusRule, setFocusRule] = useState<number | null>(null);

  // Field-picker options: core lead fields + this tenant's own custom/vertical
  // attribute keys, de-duplicated. Free-solo, so a new key can still be typed.
  const fieldOptions = useMemo(
    () => Array.from(new Set([...CORE_LEAD_FIELDS, ...customFields])),
    [customFields]
  );

  // The tenant's lead field definitions give each custom field a real type (and enum options),
  // so the rule row can offer only conditions and values that can actually match.
  const [fieldDefs, setFieldDefs] = useState<Record<string, FieldDefinition>>({});

  const kindOf = (field: string): FieldKind => {
    const core = CORE_FIELD_KIND[field];
    if (core) return core;
    const def = fieldDefs[field];
    if (!def) return 'unknown';
    return def.type === 'number'
      ? 'number'
      : def.type === 'boolean'
        ? 'boolean'
        : def.type === 'date'
          ? 'date'
          : 'text';
  };
  const valueChoices = (field: string): string[] | undefined => {
    if (ENUM_FIELD_VALUES[field]) return ENUM_FIELD_VALUES[field];
    const def = fieldDefs[field];
    if (def?.type === 'enum' && def.options?.length) return def.options;
    if (kindOf(field) === 'boolean') return BOOLEAN_VALUES;
    return undefined;
  };

  const apply = (cfg: LeadScoringConfigType) => {
    setRules((cfg.rules ?? []).map(toRuleRow));
    setHot(cfg.priorityThresholds?.hot ?? 80);
    setHigh(cfg.priorityThresholds?.high ?? 60);
    setMedium(cfg.priorityThresholds?.medium ?? 40);
    setRecencyDecayPerDay(cfg.recencyDecayPerDay ?? 0.5);
    setIsCustom(!!cfg.isCustom);
    setMissingDefaults(cfg.missingDefaults ?? []);
  };

  useEffect(() => {
    setLoading(true);
    adminApi
      .getScoringConfig()
      .then(apply)
      .catch((e) => setError(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));

    adminApi
      .getLeadScoringStats()
      .then(setStats)
      .catch(() => setStats(null));

    // The tenant's custom/vertical lead attribute keys, to enrich the field picker.
    adminApi
      .getLeadAttributeKeys()
      .then((keys: any) => setCustomFields(Array.isArray(keys) ? keys : (keys?.data ?? [])))
      .catch(() => setCustomFields([]));

    adminApi
      .getFieldDefinitions()
      .then((defs) => {
        const m: Record<string, FieldDefinition> = {};
        for (const d of defs) if (d.entity === 'lead') m[d.key] = d;
        setFieldDefs(m);
      })
      .catch(() => setFieldDefs({}));
  }, []);

  const updateRule = (i: number, patch: Partial<RuleRow>) =>
    setRules((prev) =>
      prev.map((r, idx) => {
        if (idx !== i) return r;
        const next = { ...r, ...patch };
        // Picking a different field can leave the condition/value meaningless for its type
        // (e.g. "is greater than" on a text field) — fall back to "has a value".
        if (patch.field !== undefined && patch.field !== r.field) {
          const allowed = KIND_CONDITIONS[kindOf(patch.field)];
          if (!allowed.includes(next.condition)) {
            next.condition = 'exists';
            next.valueStr = '';
          }
        }
        return next;
      })
    );
  const removeRule = (i: number) => {
    setFocusRule(null);
    setRules((prev) => prev.filter((_, idx) => idx !== i));
  };
  // New rules go to the TOP and take focus — appended at the bottom of a 13-row list the
  // button looked like it did nothing.
  const addRule = () => {
    setRules((prev) => [{ group: 'fit', field: '', condition: 'exists', valueStr: '', score: 10, description: '' }, ...prev]);
    setFocusRule(0);
  };

  const invalidRules = useMemo(
    () => rules.some((r) => !r.field.trim() || Number.isNaN(Number(r.score))),
    [rules]
  );

  // `addRows` appends rows to the saved set; `acknowledge` marks the platform defaults as
  // reconciled (add-them / dismiss), which is what clears the "defaults changed" notice.
  const save = async (opts: { addRows?: RuleRow[]; acknowledge?: boolean } = {}) => {
    setError('');
    setSaved(false);
    if (invalidRules) {
      setError('Every rule needs a field name and a numeric score.');
      return;
    }
    if (!(hot > high && high > medium)) {
      setError('Priority thresholds must satisfy hot > high > medium.');
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<LeadScoringConfigType> & { acknowledgeDefaults?: boolean } = {
        rules: [...rules, ...(opts.addRows ?? [])].map(fromRuleRow),
        ...(opts.acknowledge ? { acknowledgeDefaults: true } : {}),
        priorityThresholds: { hot, high, medium },
        recencyDecayPerDay: Number(recencyDecayPerDay) || 0,
      };
      apply(await adminApi.updateScoringConfig(payload));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void rescoreAll();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Saving a config must be reflected on existing leads, not wait for the nightly run.
  const rescoreAll = async () => {
    setRescoring(true);
    setRescoreMsg('');
    try {
      const r = await adminApi.scoreAllLeads();
      setRescoreMsg(`Re-scored ${r.scored} lead${r.scored === 1 ? '' : 's'}${r.failed ? ` (${r.failed} failed)` : ''}.`);
      setStats(await adminApi.getLeadScoringStats());
    } catch (e: any) {
      setRescoreMsg(e?.response?.data?.error?.message ?? e?.message ?? 'Re-scoring failed');
    } finally {
      setRescoring(false);
    }
  };

  const reset = async () => {
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      apply(await adminApi.resetScoringConfig());
      void rescoreAll();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Band segments for the 0–100 bar. Guard against inverted thresholds while typing.
  const t = [Math.max(0, medium), Math.max(0, high), Math.max(0, hot)].map((n) => Math.min(100, n));
  const bands = [
    { key: 'low', from: 0, to: t[0]! },
    { key: 'medium', from: t[0]!, to: Math.max(t[0]!, t[1]!) },
    { key: 'high', from: Math.max(t[0]!, t[1]!), to: Math.max(t[0]!, t[1]!, t[2]!) },
    { key: 'hot', from: Math.max(t[0]!, t[1]!, t[2]!), to: 100 },
  ] as const;

  const cardSx = { mb: 3, borderColor: '#e8ecf3', boxShadow: '0 1px 2px rgba(15,23,42,0.03)' };

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Lead Scoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Score leads 0–100; priority (Hot / High / Medium / Low) is worked out from the score automatically.
          </Typography>
        </Box>
        <Tooltip
          title={
            isCustom
              ? 'This workspace has its own rules.'
              : 'Using the platform starter rules — save to make them your own.'
          }
        >
          <span>
            <StatusChip
              status={isCustom ? 'new' : 'inactive'}
              label={isCustom ? 'Custom rules' : 'Default rules'}
              dot
              size="medium"
            />
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved. Re-scoring your leads now.
        </Alert>
      )}

      {stats && stats.totalLeads > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {(['hot', 'high', 'medium', 'low'] as const).map((b) => (
            <Box
              key={b}
              sx={{ px: 1.25, py: 0.5, borderRadius: 99, bgcolor: BAND[b].bg, color: BAND[b].fg, fontSize: '0.8125rem', fontWeight: 600 }}
            >
              {BAND[b].label}: {stats.byPriority?.[b] ?? 0}
            </Box>
          ))}
          <Box sx={{ flex: 1 }} />
          {(rescoring || rescoreMsg) && (
            <Typography variant="caption" color="text.secondary">
              {rescoring ? 'Re-scoring…' : rescoreMsg}
            </Typography>
          )}
          <Button size="small" variant="outlined" color="inherit" disabled={rescoring || saving} onClick={rescoreAll}>
            Re-score all leads now
          </Button>
        </Stack>
      )}

      {isCustom && missingDefaults.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                color="inherit"
                disabled={saving}
                onClick={() => save({ addRows: missingDefaults.map(toRuleRow), acknowledge: true })}
              >
                Add {missingDefaults.length} {missingDefaults.length === 1 ? 'rule' : 'rules'}
              </Button>
              <Button size="small" color="inherit" disabled={saving} onClick={() => save({ acknowledge: true })}>
                Dismiss
              </Button>
            </Stack>
          }
        >
          <b>The platform default rules have changed.</b> Your workspace keeps its own saved copy, so{' '}
          {missingDefaults.length} newer {missingDefaults.length === 1 ? 'rule isn’t' : 'rules aren’t'} in it:{' '}
          {missingDefaults
            .slice(0, 4)
            .map((r) => r.description || r.field)
            .join(', ')}
          {missingDefaults.length > 4 ? ` and ${missingDefaults.length - 4} more` : ''}.
        </Alert>
      )}

      {/* ── Rules ─────────────────────────────────────────────────────────── */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
          <SectionTitle
            title="Scoring rules"
            hint={`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'} · each matching rule adds its points to the lead's score`}
            action={
              <Button variant="contained" size="small" startIcon={<Add />} onClick={addRule}>
                Add rule
              </Button>
            }
          />

          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: RULE_GRID.md,
              gap: 1.5,
              px: 1.5,
              pb: 1,
            }}
          >
            <HeaderCell>If field</HeaderCell>
            <HeaderCell>Condition</HeaderCell>
            <HeaderCell>Value</HeaderCell>
            <HeaderCell>Adds</HeaderCell>
            <span />
          </Box>

          <Stack spacing={1}>
            {rules.map((r, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  border: '1px solid #e8ecf3',
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  transition: 'border-color .15s, box-shadow .15s',
                  '&:hover': { borderColor: '#d5dbe6', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: RULE_GRID,
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={fieldOptions}
                    value={r.field}
                    componentsProps={popperProps}
                    onChange={(_, v) => updateRule(i, { field: (v as string) || '' })}
                    onInputChange={(_, v) => updateRule(i, { field: v })}
                    renderInput={(params) => (
                      <TextField
                        {...(params as any)}
                        placeholder="Choose or type a field"
                        autoFocus={focusRule === i}
                        sx={inputSx}
                      />
                    )}
                  />
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={r.condition}
                    sx={inputSx}
                    onChange={(e) => updateRule(i, { condition: e.target.value as ScoringCondition })}
                  >
                    {CONDITIONS.filter(
                      (c) => KIND_CONDITIONS[kindOf(r.field)].includes(c) || c === r.condition
                    ).map((c) => (
                      <MenuItem key={c} value={c}>
                        {CONDITION_LABEL[c]}
                      </MenuItem>
                    ))}
                  </TextField>
                  {valueChoices(r.field) && (r.condition === 'equals' || r.condition === 'in') ? (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      sx={inputSx}
                      SelectProps={{ multiple: r.condition === 'in' }}
                      value={
                        r.condition === 'in'
                          ? r.valueStr.split(',').map((v) => v.trim()).filter(Boolean)
                          : r.valueStr
                      }
                      onChange={(e) => {
                        const v = e.target.value as unknown as string | string[];
                        updateRule(i, { valueStr: Array.isArray(v) ? v.join(', ') : v });
                      }}
                    >
                      {valueChoices(r.field)!.map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      size="small"
                      fullWidth
                      sx={inputSx}
                      disabled={r.condition === 'exists'}
                      placeholder={
                        r.condition === 'exists'
                          ? 'Not needed'
                          : r.condition === 'in'
                            ? 'US, UK, CA'
                            : NUMERIC_CONDS.includes(r.condition) || kindOf(r.field) === 'number'
                              ? 'A number'
                              : 'Value to match'
                      }
                      value={r.valueStr}
                      onChange={(e) => updateRule(i, { valueStr: e.target.value })}
                    />
                  )}
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    sx={inputSx}
                    value={r.score}
                    onChange={(e) => updateRule(i, { score: Number(e.target.value) })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0.25 }}>
                          <Typography variant="body2" color={r.score < 0 ? 'error.main' : 'success.main'} fontWeight={700}>
                            {r.score < 0 ? '−' : '+'}
                          </Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="text.disabled">
                            pts
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                  <Tooltip title="Remove rule">
                    <IconButton
                      size="small"
                      onClick={() => removeRule(i)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: alpha('#ef4444', 0.08) } }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, px: 0.5 }}>
                  <TextField
                    variant="standard"
                    fullWidth
                    placeholder="Add a short description (optional)"
                    value={r.description}
                    onChange={(e) => updateRule(i, { description: e.target.value })}
                    InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem', color: 'text.secondary' } }}
                  />
                  <TextField
                    select
                    size="small"
                    value={r.group}
                    onChange={(e) => updateRule(i, { group: e.target.value as ScoringGroup })}
                    sx={{ ...inputSx, width: 140, flexShrink: 0 }}
                  >
                    {GROUPS.map((g) => (
                      <MenuItem key={g.value} value={g.value}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>
            ))}

            {rules.length === 0 && (
              <Box
                sx={{
                  py: 5,
                  textAlign: 'center',
                  border: '1px dashed #dbe1ea',
                  borderRadius: '14px',
                  bgcolor: '#fafbfd',
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  No rules yet
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  With no rules saved, your workspace uses the platform's starter rules.
                </Typography>
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={addRule}>
                  Add your first rule
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* ── Priority & decay ────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
              <SectionTitle title="Priority bands" hint="The minimum score for each priority." />

              {/* 0–100 bar: shows how the thresholds carve up the score range. */}
              <Box sx={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', mb: 0.75 }}>
                {bands.map((b) => {
                  const width = Math.max(0, b.to - b.from);
                  return (
                    <Tooltip key={b.key} title={`${BAND[b.key].label}: ${b.from}–${b.to}`}>
                      <Box sx={{ width: `${width}%`, bgcolor: BAND[b.key].dot, opacity: 0.85 }} />
                    </Tooltip>
                  );
                })}
              </Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 2.5, color: 'text.disabled' }}>
                <Typography variant="caption">0</Typography>
                <Typography variant="caption">100</Typography>
              </Stack>

              <Grid container spacing={1.5}>
                {(
                  [
                    ['medium', 'Medium from', medium, setMedium],
                    ['high', 'High from', high, setHigh],
                    ['hot', 'Hot from', hot, setHot],
                  ] as const
                ).map(([band, label, value, set]) => (
                  <Grid item xs={4} key={band}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BAND[band].dot }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {label}
                      </Typography>
                    </Stack>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      value={value}
                      onChange={(e) => set(Number(e.target.value))}
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                Anything below Medium is Low.
              </Typography>

              <Box sx={{ borderTop: '1px solid #eef1f6', mt: 2.5, pt: 2.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>
                  Recency decay
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Points lost for every day without contact (counted from the last enquiry, or creation). Won/lost leads are exempt.
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  sx={{ ...inputSx, width: 160 }}
                  inputProps={{ step: 0.1, min: 0 }}
                  value={recencyDecayPerDay}
                  onChange={(e) => setRecencyDecayPerDay(Number(e.target.value))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="text.disabled">
                          pts / day
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
        {isCustom && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RestartAlt />}
            disabled={saving}
            onClick={reset}
          >
            Reset to defaults
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          disabled={saving}
          onClick={() => save()}
        >
          {saving ? 'Saving…' : 'Save rules'}
        </Button>
      </Stack>
    </Box>
  );
};

export default LeadScoringSettings;
